/**
 * Plain-text diagnostic report, designed to be PASTED verbatim.
 *
 * It began from a concrete case: the app stayed "sincronizando" forever because the
 * PowerSync service was not even running. From the screen there was no way to
 * distinguish "does not connect" from "connects but is slow", and describing it
 * by hand loses half the data.
 *
 * SINGLE SOURCE. The first version received its own object while the screen
 * rendered its rows separately: two sources for the same thing, so adding a row
 * to the UI left the report incomplete without anyone noticing. Now both consume
 * the SAME sections array: the screen renders it, this function serializes it,
 * and the hint is computed by reading it.
 *
 * Each item carries three fields for different reasons:
 *   · `label`/`value` — already-translated text: what is displayed and copied.
 *   · `status`        — used by the UI only for color; the report ignores it.
 *   · `raw`           — UNTRANSLATED value, so logic does not depend on the
 *                       language. Without it, the hint would stop firing as soon
 *                       as someone changed a translation.
 *
 * It does NOT include personal data: no email, name, or token. The code building
 * the sections is responsible for trimming identifiers.
 /** Waits for the app to finish booting (skeleton overlay disappears). */

export type DiagnosticStatus = "success" | "warning" | "danger" | "neutral";

export interface DiagnosticItem {
  /** Stable identifier, independent of language. Used by the logic. */
  key: string;
  /** Already-translated label. */
  label: string;
  /** Already-translated / formatted value, as displayed on screen. */
  value: string;
  /** UI only. */
  status?: DiagnosticStatus;
  /** Raw value for logic that must not depend on the language. */
  raw?: string | number | boolean | null;
}

export interface DiagnosticSection {
  title: string;
  items: DiagnosticItem[];
}

export interface DiagnosticsReportInput {
  appName: string;
  /** ISO timestamp. It is passed in; the clock is not read here (pure function). */
  timestamp: string;
  sections: DiagnosticSection[];
}

/** Searches by `key`, never by label: labels are translated. */
function findItem(sections: DiagnosticSection[], key: string): DiagnosticItem | undefined {
  for (const s of sections) {
    const found = s.items.find((i) => i.key === key);
    if (found) {
      return found;
    }
  }
  return undefined;
}

/**
 * Actionable hint for the pattern that started all this: there is a session
 * and synchronization does not start. It is almost always the service being
 * down, not the app, and saying so here saves looking in the wrong place.
 */
function computeHint(sections: DiagnosticSection[]): string[] {
  const sessionItem = findItem(sections, "session");
  const sync = findItem(sections, "sync");
  if (
    sessionItem?.raw !== true ||
    !sync ||
    sync.raw === "connected" ||
    sync.raw === undefined ||
    sync.raw === null
  ) {
    return [];
  }
  return [
    "",
    "## Pista",
    `La sesión está activa pero PowerSync no llega a "connected" (${String(sync.raw)}).`,
    "Comprobar que el servicio de PowerSync responde en la URL de arriba: si da",
    "502 o no responde, el problema es la pila, no la app.",
  ];
}

export function buildDiagnosticsReport(input: DiagnosticsReportInput): string {
  const parts: string[] = [`# Diagnóstico — ${input.appName}`, input.timestamp];

  for (const section of input.sections) {
    parts.push("", `## ${section.title}`);
    for (const item of section.items) {
      // An empty field is MARKED rather than omitted: in a pasted report, a
      // missing datum and one not collected read the same unless we say which it is.
      parts.push(`- ${item.label}: ${item.value === "" ? "(sin dato)" : item.value}`);
    }
  }

  parts.push(...computeHint(input.sections));
  return parts.join("\n");
}
